import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Skeleton,
  Tooltip
} from '@mui/material';
import {
  CustomTableContainer,
  CustomTable,
  CustomTableHead,
  CustomTableRow,
  CustomTableCell,
  CustomTableBody
} from '../../common';

const statusConfig = {
  graded: { label: 'Graded', bgcolor: '#ECFDF5', color: '#059669', border: 'none' },
  grading: { label: 'Grading', bgcolor: '#FFF7ED', color: '#D97706', border: 'none' },
  ongoing: { label: 'Ongoing', bgcolor: '#FFEBEA', color: '#FF502D', border: 'none' },
  resume: { label: 'Resume', bgcolor: '#EFF6FF', color: '#2563EB', border: 'none' },
  scheduled: { label: 'Upcoming', bgcolor: '#f1f5f9', color: '#64748b', border: 'none' },
  missed: { label: 'Missed', bgcolor: '#FEF2F2', color: '#DC2626', border: 'none' },
};

const formatDate = (dateVal) => {
  if (!dateVal) return 'N/A';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '');
};

const UpcomingClasses = ({ exams = [], totalAssigned = 0, loading = false }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1.5px solid #e2e8f0',
        borderRadius: '24px',
        minHeight: '420px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins' }}>
          Recent Exams
        </Typography>
        <Chip
          label={`${totalAssigned} Total Exams`}
          size="small"
          sx={{
            bgcolor: '#FFEBEA',
            color: '#FF502D',
            fontWeight: 600,
            fontSize: '11px',
            fontFamily: 'Poppins',
            borderRadius: '6px',
            height: '24px'
          }}
        />
      </Box>
      <Typography sx={{ color: '#94a3b8', fontSize: '11px', fontFamily: 'Poppins', mb: 2 }}>
        Assigned and completed exam papers
      </Typography>

      <CustomTableContainer sx={{ border: 'none', flexGrow: 1, overflow: 'visible' }}>
        <CustomTable size="small" sx={{ tableLayout: 'fixed' }}>
          <CustomTableHead>
            <CustomTableRow>
              <CustomTableCell sx={{ width: '44%', color: '#94a3b8', fontWeight: 600, fontSize: '11.5px', borderBottom: '1px solid #f1f5f9', py: 1.5, pl: 0, bgcolor: '#ffffff' }}>Exam Details</CustomTableCell>
              <CustomTableCell sx={{ width: '25%', color: '#94a3b8', fontWeight: 600, fontSize: '11.5px', borderBottom: '1px solid #f1f5f9', py: 1.5, bgcolor: '#ffffff' }}>Teacher</CustomTableCell>
              <CustomTableCell sx={{ width: '15%', color: '#94a3b8', fontWeight: 600, fontSize: '11.5px', borderBottom: '1px solid #f1f5f9', py: 1.5, bgcolor: '#ffffff' }}>Date</CustomTableCell>
              <CustomTableCell align="right" sx={{ width: '16%', borderBottom: '1px solid #f1f5f9', py: 1.5, pr: 0, bgcolor: '#ffffff' }}>Status</CustomTableCell>
            </CustomTableRow>
          </CustomTableHead>
          <CustomTableBody>
            {loading ? (
              [1, 2, 3, 4].map((i) => (
                <CustomTableRow key={i}>
                  <CustomTableCell sx={{ py: 1.5, pl: 0 }}><Skeleton width="80%" /></CustomTableCell>
                  <CustomTableCell><Skeleton width="60%" /></CustomTableCell>
                  <CustomTableCell><Skeleton width="70%" /></CustomTableCell>
                  <CustomTableCell align="right" sx={{ pr: 0 }}><Skeleton width={70} sx={{ ml: 'auto' }} /></CustomTableCell>
                </CustomTableRow>
              ))
            ) : exams.length === 0 ? (
              <CustomTableRow>
                <CustomTableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: '#94a3b8', fontFamily: 'Poppins', fontSize: '13px' }}>
                  No exams found
                </CustomTableCell>
              </CustomTableRow>
            ) : (
              exams.map((exam, idx) => {
                const cfg = statusConfig[exam.status] || statusConfig.scheduled;
                const examName = stripHtml(exam.examName);
                return (
                  <CustomTableRow
                    key={idx}
                    sx={{
                      '&:hover': { bgcolor: '#f8fafc' },
                      transition: 'background-color 0.15s ease'
                    }}
                  >
                    <CustomTableCell sx={{ borderBottom: '1px solid #eaeef3', py: 1.2, pl: 0 }}>
                      <Tooltip title={examName} placement="top" arrow>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '12px',
                            color: '#1e293b',
                            fontFamily: 'Poppins',
                            lineHeight: 1.2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'default'
                          }}
                        >
                          {examName}
                        </Typography>
                      </Tooltip>
                      <Typography sx={{ color: '#94a3b8', fontSize: '10px', fontFamily: 'Poppins', mt: 0.25, lineHeight: 1 }}>
                        ID: {exam.examId}
                      </Typography>
                    </CustomTableCell>
                    <CustomTableCell sx={{ borderBottom: '1px solid #eaeef3', py: 1.2, color: '#64748b', fontSize: '12px', fontFamily: 'Poppins' }}>
                      {exam.teacherName}
                    </CustomTableCell>
                    <CustomTableCell sx={{ borderBottom: '1px solid #eaeef3', py: 1.2 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '11.5px', color: '#1e293b', fontFamily: 'Poppins', lineHeight: 1.2 }}>
                        {formatDate(exam.date)}
                      </Typography>
                    </CustomTableCell>
                    <CustomTableCell align="right" sx={{ borderBottom: '1px solid #eaeef3', py: 1.2, pr: 0 }}>
                      <Chip
                        label={cfg.label}
                        size="small"
                        sx={{
                          bgcolor: cfg.bgcolor,
                          color: cfg.color,
                          fontWeight: 600,
                          fontSize: '11px',
                          fontFamily: 'Poppins',
                          borderRadius: '6px'
                        }}
                      />
                    </CustomTableCell>
                  </CustomTableRow>
                );
              })
            )}
          </CustomTableBody>
        </CustomTable>
      </CustomTableContainer>
    </Paper>
  );
};

export default UpcomingClasses;
